/**
 * The Indian financial year, which runs 1 April to 31 March.
 *
 * It matters here because a shop's order numbers and its invoice numbers both restart at 1
 * each year and must be gapless WITHIN the year — so the year label is part of the counter
 * key, and getting its boundary wrong would either continue last year's series or start a new
 * one in the middle of March. Task 18's invoice numbering reads the same helper.
 */

/** The month (0-indexed) the financial year begins: April. */
const FY_START_MONTH = 3;

/**
 * The financial year a moment falls in, as it is written on a bill: `2026-27`.
 *
 * The dates are read in the shop's timezone rather than the server's. A booking taken at
 * 11pm on 31 March in Chennai belongs to the year that is ending, and a server in UTC would
 * put it in the year that has not started yet.
 */
export function financialYearOf(date: Date, timeZone = 'Asia/Kolkata'): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const year = Number(parts.find((part) => part.type === 'year')?.value);
  const month = Number(parts.find((part) => part.type === 'month')?.value) - 1;

  const startYear = month >= FY_START_MONTH ? year : year - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** True when both moments fall in the same financial year. */
export function sameFinancialYear(a: Date, b: Date, timeZone?: string): boolean {
  return financialYearOf(a, timeZone) === financialYearOf(b, timeZone);
}
